import React, { useState, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, X, FileText, Loader2, Download } from "lucide-react";

const SIZE_FMT = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export const FileUploader = ({
  category = "misc",
  projectId,
  requestId,
  visibility = "private",
  accept = ".pdf,.docx,.doc,.png,.jpg,.jpeg,.xlsx,.xls,.pptx,.ppt,.csv,.txt",
  maxSizeMb = 20,
  multiple = true,
  onUploaded,
  buttonLabel = "Carica file",
  className = "",
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [items, setItems] = useState([]);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    setUploading(true);
    let done = 0;
    for (const f of list) {
      if (f.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${f.name}: file troppo grande (max ${maxSizeMb} MB)`);
        done++;
        setProgress(Math.round((done / list.length) * 100));
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      if (projectId) fd.append("project_id", projectId);
      if (requestId) fd.append("request_id", requestId);
      fd.append("visibility", visibility);
      try {
        const res = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems(prev => [...prev, res.data]);
        onUploaded && onUploaded(res.data);
        toast.success(`Caricato: ${f.name}`);
      } catch (e) {
        toast.error(`Errore upload ${f.name}: ${e.response?.data?.detail || "network error"}`);
      }
      done++;
      setProgress(Math.round((done / list.length) * 100));
    }
    setUploading(false);
    setProgress(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onChange = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div className={className}>
      <div
        onDrop={onDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="cursor-pointer cxo-card border-2 border-dashed border-slate-300 hover:border-[#2D6BFF] hover:bg-[#EAF0FF]/30 transition-colors p-5 text-center"
        data-testid={`file-upload-${category}`}
      >
        <Upload size={22} className="mx-auto text-slate-400" />
        <p className="mt-2 text-sm font-medium text-slate-700">{buttonLabel}</p>
        <p className="text-xs text-slate-500">Trascina i file qui o clicca per selezionare</p>
        <p className="text-[10px] text-slate-400 mt-1">Max {maxSizeMb} MB. Formati supportati: PDF, DOCX, PNG, JPG, XLSX, PPT</p>
        <input
          type="file"
          ref={inputRef}
          accept={accept}
          multiple={multiple}
          onChange={onChange}
          className="hidden"
          data-testid={`file-input-${category}`}
        />
      </div>
      {uploading && <Progress value={progress} className="mt-3" />}
      {items.length > 0 && (
        <div className="mt-3 space-y-2">
          {items.map(it => <UploadedFileRow key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
};

export const UploadedFileRow = ({ item, onDelete }) => {
  const token = localStorage.getItem("cxoora_token");
  const url = `${API_BASE}${item.download_url}?auth=${token}`;
  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-200">
      <FileText size={16} className="text-[#2D6BFF] shrink-0" />
      <span className="flex-1 text-sm font-medium text-slate-900 truncate">{item.name}</span>
      <span className="text-xs text-slate-500">{SIZE_FMT(item.size)}</span>
      <a href={url} target="_blank" rel="noreferrer" className="text-slate-600 hover:text-[#2D6BFF]" data-testid={`download-${item.id}`}>
        <Download size={14} />
      </a>
      {onDelete && <button onClick={() => onDelete(item)} className="text-red-500 hover:text-red-700"><X size={14} /></button>}
    </div>
  );
};

export const FileList = ({ projectId, requestId, category, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  React.useEffect(() => {
    setLoading(true);
    const params = {};
    if (projectId) params.project_id = projectId;
    if (requestId) params.request_id = requestId;
    if (category) params.category = category;
    api.get("/files/list", { params }).then(r => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [projectId, requestId, category, refreshKey]);
  const del = async (it) => {
    if (!window.confirm("Eliminare il file?")) return;
    try {
      await api.delete(`/files/${it.id}`);
      setItems(items.filter(x => x.id !== it.id));
    } catch (e) { toast.error("Errore"); }
  };
  if (loading) return <Loader2 size={16} className="animate-spin text-slate-400" />;
  if (!items.length) return <p className="text-sm text-slate-500">Nessun documento caricato.</p>;
  return (
    <div className="space-y-2">
      {items.map(it => <UploadedFileRow key={it.id} item={{ ...it, download_url: `/files/${it.id}/download` }} onDelete={del} />)}
    </div>
  );
};
